import { useEffect, useState } from 'react'
import optionService, { OPTION_CATEGORIES } from '../../services/optionService'
import {
  HiOutlinePlus, HiOutlineX, HiOutlinePencil, HiOutlineTrash,
  HiOutlineEye, HiOutlineEyeOff,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

function FormOptionManagement() {
  const [tab, setTab] = useState(OPTION_CATEGORIES[0].key)
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // option object | 'new' | null

  const load = async () => {
    setLoading(true)
    try {
      const res = await optionService.list()
      if (res.status === 'success') setData(res.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (opt) => {
    if (!window.confirm(`Xóa option "${opt.label}"?`)) return
    try {
      await optionService.remove(opt.id)
      toast.success('Đã xóa option')
      load()
    } catch { /* interceptor */ }
  }

  const toggleActive = async (opt) => {
    try {
      await optionService.update(opt.id, { is_active: !opt.is_active })
      load()
    } catch { /* interceptor */ }
  }

  const rows = data[tab] || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Cấu hình form</h1>
          <p className="page-subtitle">Quản lý các lựa chọn (select) hiển thị trong form ứng tuyển công khai</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary">
          <HiOutlinePlus className="w-5 h-5" /> Thêm option
        </button>
      </div>

      {/* Tabs theo danh mục */}
      <div className="flex flex-wrap gap-2 mb-4">
        {OPTION_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setTab(c.key)}
            className={tab === c.key
              ? 'px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white'
              : 'px-4 py-2 rounded-lg text-sm font-medium bg-white border border-secondary-200 text-secondary-600 hover:bg-secondary-50'}
          >
            {c.label} <span className="opacity-70">({(data[c.key] || []).length})</span>
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-secondary-400">Chưa có option nào trong danh mục này</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-500 border-b border-secondary-100 bg-secondary-50/50">
                  <th className="px-4 py-3 font-medium w-16">Thứ tự</th>
                  <th className="px-4 py-3 font-medium">Nhãn hiển thị</th>
                  <th className="px-4 py-3 font-medium">Giá trị lưu</th>
                  <th className="px-4 py-3 font-medium">Trạng thái</th>
                  <th className="px-4 py-3 font-medium text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-b border-secondary-50 last:border-0 hover:bg-secondary-50/40">
                    <td className="px-4 py-3 text-secondary-400">{o.sort_order}</td>
                    <td className="px-4 py-3 font-medium text-secondary-900">{o.label}</td>
                    <td className="px-4 py-3 text-secondary-500">{o.value}</td>
                    <td className="px-4 py-3">
                      {o.is_active
                        ? <span className="badge-success">Hiển thị</span>
                        : <span className="badge-neutral">Đã ẩn</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleActive(o)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title={o.is_active ? 'Ẩn' : 'Hiện'}>
                          {o.is_active ? <HiOutlineEyeOff className="w-4 h-4" /> : <HiOutlineEye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setEditing(o)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Sửa">
                          <HiOutlinePencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(o)} className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Xóa">
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <OptionFormModal
          option={editing === 'new' ? null : editing}
          defaultCategory={tab}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function OptionFormModal({ option, defaultCategory, onClose, onSaved }) {
  const isNew = !option
  const [form, setForm] = useState({
    category: option?.category || defaultCategory,
    label: option?.label || '',
    value: option?.value || '',
    sort_order: option?.sort_order ?? 0,
    is_active: option?.is_active ?? true,
  })
  const [saving, setSaving] = useState(false)
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const save = async () => {
    if (!form.label.trim()) { toast.error('Vui lòng nhập nhãn hiển thị'); return }
    setSaving(true)
    try {
      const payload = {
        category: form.category,
        label: form.label.trim(),
        value: form.value.trim() || form.label.trim(),
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      }
      if (isNew) {
        await optionService.create(payload)
        toast.success('Đã thêm option')
      } else {
        await optionService.update(option.id, payload)
        toast.success('Đã cập nhật')
      }
      onSaved()
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-md p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">{isNew ? 'Thêm option' : 'Sửa option'}</h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Danh mục</label>
            <select className="select" value={form.category} onChange={(e) => setField('category', e.target.value)}>
              {OPTION_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Nhãn hiển thị <span className="text-red-500">*</span></label>
            <input className="input" value={form.label} onChange={(e) => setField('label', e.target.value)} placeholder="VD: Chuyên viên Kinh doanh" />
          </div>
          <div>
            <label className="label">Giá trị lưu (để trống = giống nhãn)</label>
            <input className="input" value={form.value} onChange={(e) => setField('value', e.target.value)} placeholder="VD: chuyen-vien-kd" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Thứ tự</label>
              <input type="number" className="input" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
            </div>
            <div>
              <label className="label">Trạng thái</label>
              <select className="select" value={form.is_active ? '1' : '0'} onChange={(e) => setField('is_active', e.target.value === '1')}>
                <option value="1">Hiển thị</option>
                <option value="0">Ẩn</option>
              </select>
            </div>
          </div>

          <button onClick={save} disabled={saving} className="btn-primary w-full mt-2">
            {saving ? 'Đang lưu...' : (isNew ? 'Thêm option' : 'Lưu thay đổi')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FormOptionManagement
